/**
 * Which frame the shell draws (W-F shell wave). Author: Mahmoud Ragab Ahmed.
 *
 * - desktop (over 900px): the side menu, expanded or collapsed to the icon rail by the
 *   person's own persisted choice.
 * - tablet (721px to 900px): the icon rail always; hovering, focusing or the toggle opens
 *   it as an overlay that pushes nothing aside.
 * - phone (720px and under): a top bar with a menu button; the side menu is a drawer that
 *   slides over the screen.
 *
 * The CSS keys the same breakpoints (shell.css, "the frame"); this hook tells the
 * components which BEHAVIOUR to run. Where matchMedia is absent (the unit-test DOM) the
 * frame is desktop, which is what every existing test assumes.
 */
import { useSyncExternalStore } from "react";

export type ShellLayout = "desktop" | "tablet" | "phone";

export const PHONE_QUERY = "(max-width: 720px)";
export const TABLET_QUERY = "(min-width: 721px) and (max-width: 900px)";

function hasMatchMedia(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

function subscribe(onChange: () => void): () => void {
  if (!hasMatchMedia()) {
    return () => undefined;
  }
  const queries = [window.matchMedia(PHONE_QUERY), window.matchMedia(TABLET_QUERY)];
  for (const query of queries) {
    query.addEventListener("change", onChange);
  }
  return () => {
    for (const query of queries) {
      query.removeEventListener("change", onChange);
    }
  };
}

function snapshot(): ShellLayout {
  if (!hasMatchMedia()) {
    return "desktop";
  }
  if (window.matchMedia(PHONE_QUERY).matches) {
    return "phone";
  }
  return window.matchMedia(TABLET_QUERY).matches ? "tablet" : "desktop";
}

export function useShellLayout(): ShellLayout {
  return useSyncExternalStore(subscribe, snapshot, () => "desktop");
}
